import { t } from "@travily/i18n";
import { fieldSet } from "@ui/components/Field/field.styles";
import { createContext, type ReactNode, use, useId } from "react";

export type FieldSetSize = "md" | "lg" | "xl";

interface FieldSetListContextValue {
  size: FieldSetSize;
  showDivider: boolean;
}

const FieldSetListContext = createContext<FieldSetListContextValue>({ size: "lg", showDivider: false });

interface FieldSetItemContextValue {
  size: FieldSetSize;
  legendId: string;
  isDisabled: boolean;
}

const FieldSetItemContext = createContext<FieldSetItemContextValue | null>(null);

function useFieldSetItem(componentName: string): FieldSetItemContextValue {
  const ctx = use(FieldSetItemContext);
  if (!ctx) throw new Error(`${componentName} must be used within a FieldSet`);
  return ctx;
}

export interface FieldSetListProps {
  children: ReactNode;
  className?: string;
  /** Render a border-bottom + padding-bottom between sections. Defaults to true. */
  dividers?: boolean;
  size?: FieldSetSize;
}

function FieldSetList({ children, className, dividers = true, size = "lg" }: FieldSetListProps) {
  const styles = fieldSet({ size });
  return (
    <FieldSetListContext value={{ size, showDivider: dividers }}>
      <div className={styles.list({ className })}>{children}</div>
    </FieldSetListContext>
  );
}

export interface FieldSetProps {
  children: ReactNode;
  className?: string;
  size?: FieldSetSize;
  isDisabled?: boolean;
  /** Render as `<div role="group">` instead of `<fieldset>` — escape hatch when nesting another fieldset-emitting child (e.g. RadioGroup). */
  as?: "fieldset" | "div";
}

function FieldSetRoot({ children, className, size, isDisabled = false, as = "fieldset" }: FieldSetProps) {
  const { size: listSize, showDivider } = use(FieldSetListContext);
  const effectiveSize = size ?? listSize;
  const legendId = useId();
  const styles = fieldSet({ size: effectiveSize, isDisabled });

  const itemClass = showDivider ? styles.item() : undefined;
  const rootClass = [styles.root(), itemClass, className].filter(Boolean).join(" ");

  const content = (
    <FieldSetItemContext value={{ size: effectiveSize, legendId, isDisabled }}>{children}</FieldSetItemContext>
  );

  if (as === "div") {
    return (
      // biome-ignore lint/a11y/useSemanticElements: `as="div"` is the escape hatch for nesting another fieldset-emitting child (e.g. RadioGroup) without producing invalid double-<fieldset> markup
      <div aria-labelledby={legendId} className={rootClass} role="group">
        {content}
      </div>
    );
  }
  return (
    <fieldset aria-labelledby={legendId} className={rootClass} disabled={isDisabled || undefined}>
      {content}
    </fieldset>
  );
}

export interface FieldSetLegendProps {
  as?: "h2" | "h3" | "h4";
  children: ReactNode;
  className?: string;
  isOptional?: boolean;
}

function FieldSetLegend({ as: Component = "h3", children, className, isOptional }: FieldSetLegendProps) {
  const ctx = useFieldSetItem("FieldSet.Legend");
  const styles = fieldSet({ size: ctx.size });
  return (
    <Component className={styles.legend({ className })} id={ctx.legendId}>
      {children}
      {isOptional && <span className={styles.optional()}>{t("components.field_set.optional")}</span>}
    </Component>
  );
}

export interface FieldSetDescriptionProps {
  children: ReactNode;
  className?: string;
}

function FieldSetDescription({ children, className }: FieldSetDescriptionProps) {
  const ctx = useFieldSetItem("FieldSet.Description");
  const styles = fieldSet({ size: ctx.size });
  return <p className={styles.description({ className })}>{children}</p>;
}

export const FieldSet = Object.assign(FieldSetRoot, {
  List: FieldSetList,
  Legend: FieldSetLegend,
  Description: FieldSetDescription,
});
